import * as React from "react";
import { graphql } from "gatsby";
import Layout from "../components/layout";
import Seo from "../components/seo";
import Hero from "../components/hero";
import Banner from "../components/banner";
import Presentation from "../components/presentation";
import Questions from "../components/questions";
import Slideshow from "../components/slideshow";
import Articles from "../components/articles";
import ImageWithText from "../components/image-with-text";
import SlideshowProducts from "../components/slideshow-products";
import { useIntl } from "react-intl";

const DestinationTemplate = ({ data }) => {
  const {
    title,
    seo,
    mainTitle,
    products,
    intro,
    featuredImage,
    banner,
    questions,
    quotationsTitle,
    quotations,
    articles,
    seoSection,
    noIndex,
    hideProducts,
  } = data.destination;

  const seoTitle = seo?.title || title;
  const seoDescription = seo?.description?.description || intro?.intro;
  const seoImage = seo?.image || featuredImage;
  const canonicalURL = seo?.canonicalURL;
  const { formatMessage } = useIntl();

  return (
    <Layout whiteHeader={featuredImage}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        canonicalURL={canonicalURL}
        noIndex={noIndex}
      />
      <Hero
        title={title}
        intro={intro?.intro}
        media={featuredImage}
        pageTitle={false}
        reviews={true}
        badges={data.destination.badges}
      />
      <Banner
        heading={banner?.heading}
        text={banner?.text}
        link={banner?.link}
        linkText={banner?.linkText}
        active={banner?.active}
        buttonLink={banner?.buttonLink}
      />
      <Presentation destination={data.destination} title={mainTitle} />
      {!hideProducts && (
        <SlideshowProducts
          products={products}
          title={formatMessage({ id: "chooseOther" })}
          variant="white"
        />
      )}
      <Questions questions={questions} destination />
      <Slideshow
        slides={quotations}
        quotations
        title={quotationsTitle?.quotationsTitle}
        pictures={data.translations.ratingsImages}
        heading
      />
      <Articles articles={articles} heading />
      <ImageWithText
        image={seoSection?.image}
        mobileImage={seoSection?.mobileImage}
        imageAlt={seoSection?.imageAlt}
        text={seoSection?.text}
        link={seoSection?.link}
        modal={seoSection?.modal}
        className="full-width"
      />
    </Layout>
  );
};

export default DestinationTemplate;

export const query = graphql`
  query ($id: String!, $locale: String!) {
    destination: contentfulDestination(
      contentful_id: { eq: $id }
      node_locale: { eq: $locale }
    ) {
      title
      slug
      mainTitle
      hideProducts
      hideBadge
      noIndex
      seo {
        canonicalURL
        title
        description {
          description
        }
        image {
          description
          title
          gatsbyImageData(
            width: 1200
            height: 1200
            quality: 80
            resizingBehavior: FILL
          )
        }
      }
      category {
        title
      }
      dateRange
      badges {
        label
        backgroundColor
        opacity
        textColor
      }
      products: product {
        contentful_id
        subtitleCarousel
        destination {
          slug
          title
          products: product {
            contentful_id
          }
        }
        order
        referenceProduit
        stayReference
        featuredImage {
          description
          gatsbyImageData(quality: 80)
          title
        }
        intro {
          intro
        }
        duration
        distance
        difficulty
        price
        badges {
          label
          backgroundColor
          opacity
          textColor
        }
      }
      intro {
        intro
      }
      featuredImage {
        description
        gatsbyImageData(quality: 80)
        title
        file {
          details {
            image {
              width
              height
            }
          }
        }
      }
      badge {
        description
        title
        file {
          url
          details {
            image {
              width
              height
            }
          }
        }
      }
      banner {
        heading
        text {
          raw
        }
        link
        linkText
        active
        buttonLink {
          id
          button
          buttonBackgroundColour
          textColour
          title
          link
          target
          modal {
            ...Modal
          }
        }
      }
      presentation {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            __typename
            description
            gatsbyImageData(quality: 80)
          }
        }
      }
      questions {
        contentful_id
        title
        category {
          title
        }
        content {
          raw
          references {
            ... on ContentfulAsset {
              contentful_id
              __typename
              description
              gatsbyImageData(quality: 80)
            }
          }
        }
      }
      quotationsTitle {
        quotationsTitle
      }
      quotations {
        contentful_id
        image {
          description
          title
          gatsbyImageData(quality: 80)
        }
        mobileImage {
          title
          gatsbyImageData(quality: 80)
        }
        imageAlt
        text {
          raw
        }
        link
      }
      articles {
        contentful_id
        publishedAt
        title
        slug
        featuredImage {
          description
          gatsbyImageData(quality: 80)
          title
        }
        publishedAt
        excerpt {
          excerpt
        }
      }
      seoSection {
        image {
          description
          title
          gatsbyImageData(quality: 80)
        }
        mobileImage {
          title
          gatsbyImageData(quality: 80)
        }
        imageAlt
        text {
          raw
        }
        link
        modal {
          ...Modal
        }
      }
    }

    translations: contentfulTranslations(node_locale: { eq: $locale }) {
      translations {
        key
        value
      }
      ratingsImages {
        title
        description
        gatsbyImageData(
          width: 300
          height: 300
          quality: 80
          resizingBehavior: SCALE
        )
      }
    }
  }
`;
